import PropTypes from "prop-types";
import React, { useContext, useEffect } from "react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import { Formik, Form } from "formik";
import { Placeholder } from "rsuite";
import {
  countryCodes,
  StyleTypeContext,
  TranslationContext,
} from "../../../umbilical_tools";
import { SelectField } from "../../forms/inputGroup/helpers";
import { InputField } from "../../forms/inputGroup";
import { adtFields, chdFields, groupLeaderFields } from "./helpers";
import jssVariables from "../../../jss_setup";
import { getGuestsFormInfoSelector } from "../../../selectors/booking_process/guests";
import { useSelector } from "react-redux";
import update from "immutability-helper";

const fieldCustomStyles = {
  label: {
    textTransform: "capitalize",
  },
  input: {
    border: "1px solid lightgray",
    smallscreen: {
      height: "3.2rem",
    },
  },
};

const formStyles = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gridGap: `calc(${jssVariables.normal_gap} * 1)`,
  justifyContent: "space-between",
  padding: `calc(${jssVariables.normal_gap} * 2)`,
};

function gFormToRequirements(form) {
  return Object.keys(form).reduce((a, v) => ({ ...a, [v]: true }), {});
}

function gRequirementsToForm(form) {
  return Object.keys(form)
    .filter(
      (key) =>
        form[key] &&
        ![
          "service_type",
          "guest_type",
          "lead_guest",
          "group_leader",
          "room_leader",
        ].includes(key)
    )
    .reduce((a, v) => ({ ...a, [v]: "" }), {});
}

const travellerFormStyles = createUseStyles({
  travellerForm: (props) => ({
    borderRadius: "10px",
    border: `2px solid ${jssVariables.colors.base_color}`,
    ..._.get(props, "customStyles.travellerForm", {}),
  }),
  placeholdersContainers: (props) => ({
    padding: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.placeholdersContainers", {}),
  }),
  guestHeader: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    justifyContent: "space-between",
    textTransform: "capitalize",
    fontSize: "large",
    fontWeight: "bold",
    padding: `0 calc(${jssVariables.normal_gap} * 2)`,
    paddingTop: jssVariables.normal_gap,
    ..._.get(props, "customStyles.guestHeader", {}),
  }),
  formLeader: (props) => ({
    ...formStyles,
    ..._.get(props, "customStyles.formLeader", {}),
  }),
  formADT: (props) => ({
    ...formStyles,
    ..._.get(props, "customStyles.formADT", {}),
  }),
  formCHD: (props) => ({
    ...formStyles,
    ..._.get(props, "customStyles.formCHD", {}),
  }),
  row: (props) => ({
    ..._.get(props, "customStyles.row", {}),
  }),
  requiredFieldsLabel: (props) => ({
    color: jssVariables.colors.border,
    fontSize: "small",
    ..._.get(props, "customStyles.requiredFieldsLabel", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    travellerForm: (props) => ({
      border: `2px solid ${jssVariables.colors.base_color}`,
      padding: jssVariables.normal_gap,
      borderRadius: "10px",
      width: "45rem",
      justifySelf: "center",
      ..._.get(props, "customStyles.travellerForm.smallscreen", {}),
    }),
    formLeader: (props) => ({
      gridTemplateColumns: "1fr 2fr 2fr",
      ..._.get(props, "customStyles.formLeader.smallscreen", {}),
    }),
    formADT: (props) => ({
      gridTemplateColumns: "1fr 2fr 2fr",
      ..._.get(props, "customStyles.formADT.smallscreen", {}),
    }),
    formCHD: (props) => ({
      gridTemplateColumns: "repeat(2, 1fr)",
      ..._.get(props, "customStyles.formCHD.smallscreen", {}),
    }),
    row: (props) => ({
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridColumn: "span 3",
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.row.smallscreen", {}),
    }),
  },
});
const TravellerForm = ({
  ghost,
  idx,
  type,
  guest_id,
  guest_uid,
  group_leader,
  guests,
  setGuests,
}) => {
  const { type: styleType, brand, cStyler } = useContext(StyleTypeContext);

  const { t } = useContext(TranslationContext);
  const { leadAdultRequiredFields, adultRequiredFields, childRequiredFields } =
    useSelector((state) =>
      getGuestsFormInfoSelector(state, { srvType: "TRF" })
    );

  const guestType = { ADT: t("adult"), CHD: t("child"), INF: t("infant") };

  const requiredFields = group_leader
    ? !_.isEmpty(leadAdultRequiredFields)
      ? leadAdultRequiredFields
      : gFormToRequirements(groupLeaderFields)
    : type === "ADT"
    ? !_.isEmpty(adultRequiredFields)
      ? adultRequiredFields
      : gFormToRequirements(adtFields)
    : !_.isEmpty(childRequiredFields)
    ? childRequiredFields
    : gFormToRequirements(chdFields);

  const customStyles = cStyler({
    type: styleType,
    brand,
    objName: "TrfTravellerForm",
  });
  const classes = travellerFormStyles({ guestType: type, ghost, customStyles });

  function getInitialValues() {
    const currentGuest = guests.find((g) => g.guest_id === guest_id);
    if (currentGuest) {
      return { ...currentGuest, guest_type: type };
    }

    const commonGuestAttrs = {
      guest_id,
      guest_uid,
      guest_type: type,
      nationality: "",
    };

    if (group_leader) {
      return {
        ...commonGuestAttrs,
        ...(!_.isEmpty(leadAdultRequiredFields)
          ? gRequirementsToForm(leadAdultRequiredFields)
          : groupLeaderFields),
        group_leader,
      };
    } else if (type === "ADT") {
      return {
        ...commonGuestAttrs,
        ...(!_.isEmpty(adultRequiredFields)
          ? gRequirementsToForm(adultRequiredFields)
          : adtFields),
      };
    } else {
      return {
        ...commonGuestAttrs,
        ...(!_.isEmpty(childRequiredFields)
          ? gRequirementsToForm(childRequiredFields)
          : chdFields),
        title: "CH",
      };
    }
  }

  useEffect(() => {
    const undefinedGuest =
      typeof (guests || []).find((g) => g.guest_id === guest_id) ===
      "undefined";
    if (guests && undefinedGuest) {
      setGuests((p) => {
        return update(p, {
          $apply: (gsts) =>
            gsts.find((g) => g.guest_id === guest_id)
              ? gsts
              : gsts.concat(getInitialValues()),
        });
      });
    }
  }, []);

  return (
    <div className={classes.travellerForm}>
      {ghost ? (
        <div className={classes.placeholdersContainers}>
          <Placeholder.Paragraph rows={1} active />
          <Placeholder.Paragraph rows={2} active />
          <Placeholder.Paragraph rows={2} active />
          <Placeholder.Paragraph rows={2} active />
          <Placeholder.Paragraph rows={2} active />
          <Placeholder.Paragraph rows={2} active />
          <Placeholder.Paragraph rows={2} active />
          <Placeholder.Paragraph rows={1} active />
        </div>
      ) : (
        <React.Fragment>
          <span className={classes.guestHeader}>
            {group_leader ? (
              <span>{t("leadGuest")}</span>
            ) : (
              <span>{`${guestType[type]} - ${idx + 1}`}</span>
            )}
            <span>{`${guestType[type]} - ${idx + 1}`}</span>
            <small className={classes.requiredFieldsLabel}>
              {t("requiredFields")}
            </small>
          </span>
          <Formik
            initialValues={getInitialValues()}
            validateOnChange={false}
            validateOnBlur={true}
            validate={(values) => {
              const currentGuestIdx = guests.findIndex(
                (g) => g.guest_uid === values.guest_uid
              );

              const guestsTemp = [...guests];
              if (currentGuestIdx >= 0) {
                guestsTemp[currentGuestIdx] = {
                  ...guestsTemp[currentGuestIdx],
                  ...values,
                };
              } else {
                guestsTemp.push(values);
              }
              setGuests(guestsTemp);
            }}>
            <Form
              className={
                group_leader
                  ? classes.formLeader
                  : type === "ADT"
                  ? classes.formADT
                  : classes.formCHD
              }>
              {requiredFields.title && (
                <SelectField
                  id={`title_${idx}`}
                  label={t("title")}
                  name="title"
                  options={[
                    ["DR", "Dr."],
                    ["MR", "Mr."],
                    ["MRS", "Mrs."],
                    ["MS", "Ms."],
                  ]}
                  customStyles={{
                    ...fieldCustomStyles,
                    ..._.get(customStyles, "inputGroupField", {}),
                  }}
                  placeholder="----"
                />
              )}
              <InputField
                id={`first_name_${idx}`}
                as="input"
                label={t("firstName")}
                name="first_name"
                customStyles={{
                  ...fieldCustomStyles,
                  ..._.get(customStyles, "inputGroupField", {}),
                }}
                placeholder="----"
              />
              <InputField
                id={`last_name_${idx}`}
                as="input"
                label={t("lastName")}
                name="last_name"
                customStyles={{
                  ...fieldCustomStyles,
                  ..._.get(customStyles, "inputGroupField", {}),
                }}
                placeholder="----"
              />
              <div className={classes.row}>
                {requiredFields.phone && (
                  <InputField
                    id={`phone_${idx}`}
                    as="input"
                    label={t("phone")}
                    name="phone"
                    customStyles={{
                      ...fieldCustomStyles,
                      ..._.get(customStyles, "inputGroupField", {}),
                    }}
                    placeholder="----"
                  />
                )}
                {requiredFields.email && (
                  <InputField
                    id={`email_${idx}`}
                    as="input"
                    label={t("email")}
                    name="email"
                    customStyles={{
                      ...fieldCustomStyles,
                      ..._.get(customStyles, "inputGroupField", {}),
                    }}
                    placeholder="----"
                  />
                )}
              </div>
              {requiredFields.nationality && (
                <div className={classes.row}>
                  <SelectField
                    id={`nationality_${idx}`}
                    label={t("nationality")}
                    name="nationality"
                    options={Object.entries(countryCodes).map(
                      ([key, value]) => [value, `${key} (${value})`]
                    )}
                    customStyles={{
                      ...fieldCustomStyles,
                      ..._.get(customStyles, "inputGroupField", {}),
                    }}
                    placeholder="----"
                  />
                </div>
              )}
            </Form>
          </Formik>
        </React.Fragment>
      )}
    </div>
  );
};
TravellerForm.defaultProps = {
  ghost: false,
  idx: 0,
  type: "ADT",
  guest_id: "",
  guest_uid: "",
  group_leader: false,
  guests: {},
};
TravellerForm.propTypes = {
  ghost: PropTypes.bool,
  idx: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  guest_id: PropTypes.string.isRequired,
  guest_uid: PropTypes.string.isRequired,
  group_leader: PropTypes.bool.isRequired,
  guests: PropTypes.array.isRequired,
  setGuests: PropTypes.func.isRequired,
};
export default TravellerForm;
